/* Syba variant-id integrity guard — the add-to-cart POST must carry the
 * variant the customer SEES selected.
 *
 * ROOT CAUSE (2026-07-18, reproduced with stack-trace instrumentation)
 * Kaching Bundles re-stamps the product form's hidden input[name="id"] from
 * its own internal variant state — both when a deal bar is clicked
 * (rn._updateIdInput via updateItem) and again on add-to-cart
 * (_refreshItemsBeforeAddToCart). That internal state only learns about
 * variant switches from the theme's `variantChange` publish, which Dawn fires
 * AFTER the Section-Rendering fetch resolves (a full network round-trip).
 * A customer who clicks fast — switch variant, then a deal / ATC within
 * ~500ms — gets the PREVIOUS variant's id silently written back into the
 * form while the variant picker UI still shows their new choice.
 * Observed live: select 20L → click 3x quickly → cart receives 5L × 3.
 *
 * THE FIX
 * One document-level submit listener in the CAPTURE phase. For any
 * form posting to /cart/add, re-assert input[name="id"] from the section's
 * own <variant-radios>/<variant-selects> `currentVariant` — the property
 * Dawn updates SYNCHRONOUSLY in the change handler, i.e. the same source of
 * truth as the picker UI. Ordering makes this bulletproof:
 *   - Kaching's pre-submit writes happen on the ATC *click* (or on form
 *     listeners registered after ProductForm's) — all before submit-capture.
 *   - Dawn's ProductForm serializes FormData in its own form-level submit
 *     listener — target phase, i.e. AFTER document capture.
 * So this guard runs after every stale write and before serialization.
 *
 * SAFETY
 *   - No-op when currentVariant is unset (variant never changed → the
 *     server-rendered id is correct by definition).
 *   - No-op when the ids already agree (the normal, slow-clicking case).
 *   - Scoped to /cart/add forms only (installment/payment forms untouched).
 *   - On this store Kaching deals never map to a different variant (deal ids
 *     are identical across variants — verified), so the picker is always the
 *     correct authority. If a variant-mapping deal type is ever introduced,
 *     revisit this file.
 * Report upstream to Kaching: deal-click + pre-ATC id restamp use stale
 * internal variant state when the switch happened < ~500ms earlier.
 */
(function () {
  'use strict';
  if (window.__sybaVariantIdIntegrity) return; // idempotent under double-include
  window.__sybaVariantIdIntegrity = true;

  var stats = { checked: 0, corrected: 0, lastCorrection: null };
  window.__sybaVariantIdIntegrityStats = stats;

  document.addEventListener(
    'submit',
    function (ev) {
      try {
        var form = ev.target;
        if (!form || !form.getAttribute) return;
        var action = form.getAttribute('action') || '';
        if (action.indexOf('/cart/add') === -1) return;
        var idInput = form.querySelector('input[name="id"]');
        if (!idInput) return;
        // The picker lives in the same product info container as the form —
        // both on the PDP and inside the quick-add modal (injected PDP HTML).
        var scope =
          form.closest('.product__info-container') ||
          form.closest('product-info') ||
          form.closest('section') ||
          document;
        var vr = scope.querySelector('variant-radios, variant-selects');
        var cv = vr && vr.currentVariant;
        stats.checked++;
        if (!cv || !cv.id) return; // variant never changed in this view — server-rendered id is authoritative
        if (String(idInput.value) === String(cv.id)) return;
        stats.corrected++;
        stats.lastCorrection = { from: String(idInput.value), to: String(cv.id) };
        idInput.value = cv.id;
      } catch (e) {
        /* never break add-to-cart over the guard */
      }
    },
    true
  );
})();
